<script setup lang="ts">
import { HorizontalProps } from "~/components/Banner/DiscountPromo/Horizontal.props"
import type { WithContext, Offer } from "schema-dts"
import { SeoBaseProps } from "~/components/seo/Base.props"

const route = useRoute()
const { locale } = useI18n()

const { data: promotionInternalData, error } = await usePage(
  `/api/gluon/fetchPromotionInternalPage/${locale.value}/${
    route.params.slug as string
  }/promotion_internal.js`
)

const { data: productsEndPromotion } = await useFetch(
  "/api/getProductsEndPromotion"
)

if (error.value) {
  throw createError({
    message: error.value.message,
    statusCode: error.value.statusCode ?? 404
  })
}

const posterDiscountCode = null
// TODO: dynamic discount promo code
// const posterDiscountCode: HorizontalProps = {
//   title: "È il tuo primo ordine?",
//   text: "Approfitta subito dello **sconto del 5%** sul tuo primo acquisto.",
//   bgImageLarge: getImageData({
//     altText: "Placeholder",
//     url: "https://picsum.photos/id/703/348/397",
//     width: 348,
//     height: 397
//   }),
//   cta: { text: "Vai alla pagina" },
//   cardType: "withPromoCode",
//   promoCode: "SCONTO10",
//   promoCodeInfoText: `CLICCA SUL CODICE
//   PER INSERIRLO GIÀ NEL CARRELLO`,
//   promoCodeInfoTextCopied: "BEN FATTO!",
//   promoTextCopied: "Codice inserito nel carrello"
// }
const promotionListWithDiscount = computed(() =>
  posterDiscountCode && promotionInternalData.value?.page.promotionsList
    ? [
        ...promotionInternalData.value?.page.promotionsList?.slice(0, 5),
        posterDiscountCode,
        ...promotionInternalData.value?.page.promotionsList?.slice(5)
      ]
    : promotionInternalData.value?.page.promotionsList
)

const isStillActive = computed(() => {
  if (!promotionInternalData.value?.page) return false

  return isPromotionActive(
    promotionInternalData.value.page.startDate,
    promotionInternalData.value.page.endDate
  )
})

if (
  !isStillActive.value &&
  !promotionInternalData.value?.page?.showWhenExpired
) {
  await navigateTo(customPaths.promotions, {
    redirectCode: 302
  })
}

//Structured data of Schema.org, (https://lafarmacia.atlassian.net/wiki/spaces/TF/pages/152305666/Pagine+Promo+Struttura+desiderata)
const baseUrl = useRuntimeConfig().public.BASE_URL_APP
const jsonLd = computed<WithContext<Offer>>(() => ({
  "@type": "Offer",
  "@context": "https://schema.org",
  availabilityStarts: promotionInternalData.value?.page.startDate,
  availabilityEnds: promotionInternalData.value?.page.endDate,
  name: promotionInternalData.value?.page.promotionName,
  offeredBy: {
    "@type": "Organization",
    name: "TopFarmacia",
    logo: `${baseUrl}/images/LogoTopFarmaciaOnline.svg`
  },
  ...(promotionInternalData.value?.page?.promoBanner?.backgroundImage?.image
    ?.url
    ? {
        image: {
          "@type": "ImageObject",
          image:
            promotionInternalData.value?.page.promoBanner?.backgroundImage.image
              .url
        }
      }
    : {}),
  description: promotionInternalData.value?.page.description,
  availability: isStillActive.value
    ? "https://schema.org/InStock"
    : "https://schema.org/SoldOut"
}))

useHead({
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify(cleanJsonLdObject(jsonLd.value))
    }
  ]
})

const seoData = computed((): SeoBaseProps => {
  return {
    ...promotionInternalData.value?.seo,
    metaTitle:
      `${promotionInternalData.value?.seo.metaTitle} - Pagina ${route.params.page}` ??
      undefined,
    metaDescription:
      `${promotionInternalData.value?.seo.metaDescription} - Pagina ${route.params.page}` ??
      undefined
  }
})
</script>

<template>
  <div v-if="promotionInternalData">
    <SeoBase v-if="promotionInternalData.seo" v-bind="seoData" />
    <PagesPromotionInternal
      v-if="isStillActive"
      v-bind="promotionInternalData.page"
      :promotions-list="promotionListWithDiscount"
    />
    <PagesPromoExpired
      v-else-if="productsEndPromotion"
      :products="productsEndPromotion"
      :message="promotionInternalData?.page.textEndPromotion"
      :end-date="promotionInternalData?.page.endDate"
      :start-date="promotionInternalData?.page.startDate"
    />
  </div>
</template>
